// Single source of truth for portfolio projects + case studies.

export const PROJECTS = [
  {
    slug: "techstart-solutions",
    title: "TechStart Solutions",
    category: "Website Design",
    desc: "Conversion-focused SaaS landing page that increased qualified leads by 3x in 60 days.",
    tag: "SaaS",
    impact: "+212% leads",
    client: "TechStart Solutions",
    industry: "B2B SaaS",
    duration: "5 weeks",
    image: "https://images.unsplash.com/photo-1467232004584-a241de8bcf5d?w=1400&h=800&fit=crop",
    gallery: [
      "https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=900&h=600&fit=crop",
      "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=900&h=600&fit=crop",
      "https://images.unsplash.com/photo-1517245386807-bb43f82c33c4?w=900&h=600&fit=crop",
    ],
    challenge: "TechStart was relying on paid ads to drive demos, but their landing page converted at only 1.8%. CAC was eating into runway and signups had plateaued.",
    solution: "We redesigned the entire funnel: a punchier hero with one CTA, social proof above the fold, a 90-second product video, and an instant-book demo widget. We rewrote every section using AI-assisted copy and ran 7 split tests over four weeks.",
    results: [
      { metric: "+212%", label: "Qualified leads" },
      { metric: "−41%", label: "CAC" },
      { metric: "4.6%", label: "Landing CVR" },
      { metric: "32%", label: "Demo-show rate" },
    ],
    testimonial: {
      quote: "We replaced four agencies with Optivize and got more output in six weeks than the previous year combined.",
      name: "Rajesh Kumar",
      role: "CEO, TechStart Solutions",
    },
    services: ["Website Design", "Copywriting", "Conversion Rate Optimization"],
  },
  {
    slug: "fashion-hub-store",
    title: "Fashion Hub Store",
    category: "Ecommerce",
    desc: "Custom Shopify-grade storefront with auto-inventory sync, abandoned cart and loyalty.",
    tag: "Ecommerce",
    impact: "3x sales",
    client: "Fashion Hub",
    industry: "Apparel D2C",
    duration: "8 weeks",
    image: "https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?w=1400&h=800&fit=crop",
    gallery: [
      "https://images.unsplash.com/photo-1483985988355-763728e1935b?w=900&h=600&fit=crop",
      "https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=900&h=600&fit=crop",
      "https://images.unsplash.com/photo-1490481651871-ab68de25d43d?w=900&h=600&fit=crop",
    ],
    challenge: "Fashion Hub was running on a templated store with slow load times and no abandoned-cart flow. Their conversion rate was 0.9% and 73% of carts were being abandoned.",
    solution: "Built a custom headless storefront on a React + Next.js stack, integrated Razorpay + COD with smart routing, added a 3-step abandoned-cart sequence (email → WhatsApp → SMS), and launched a points-based loyalty program.",
    results: [
      { metric: "3x", label: "Sales (90d)" },
      { metric: "2.7%", label: "Store CVR" },
      { metric: "−48%", label: "Cart abandonment" },
      { metric: "₹4.2L", label: "Loyalty revenue/mo" },
    ],
    testimonial: {
      quote: "The team at Optivize.digital is exceptional. They built us a beautiful e-commerce website and our sales have tripled!",
      name: "Priya Sharma",
      role: "Marketing Director, Fashion Hub",
    },
    services: ["Ecommerce Development", "Payments", "Email + WhatsApp Automation"],
  },
  {
    slug: "edutech-pro",
    title: "EduTech Pro",
    category: "API Integration",
    desc: "Connected 6 LMS APIs (Moodle, Canvas, Coursera) into a single course-author dashboard.",
    tag: "EdTech",
    impact: "−2,400 hrs/yr",
    client: "EduTech Pro",
    industry: "EdTech SaaS",
    duration: "6 weeks",
    image: "https://images.unsplash.com/photo-1555066931-4365d14bab8c?w=1400&h=800&fit=crop",
    gallery: [
      "https://images.unsplash.com/photo-1517694712202-14dd9538aa97?w=900&h=600&fit=crop",
      "https://images.unsplash.com/photo-1531403009284-440f080d1e12?w=900&h=600&fit=crop",
      "https://images.unsplash.com/photo-1551434678-e076c223a692?w=900&h=600&fit=crop",
    ],
    challenge: "Course authors were copying content across 6 LMS platforms manually, a 4-hour-per-course exercise. The ops team was the bottleneck for launching new programs.",
    solution: "Built a unified API gateway with adapters for Moodle, Canvas, Coursera, Teachable, Thinkific, and Kajabi. Added a single drag-and-drop dashboard that publishes to all platforms with one click.",
    results: [
      { metric: "−2,400", label: "Hours saved/yr" },
      { metric: "1-click", label: "Publish to 6 LMS" },
      { metric: "−92%", label: "Time-to-launch" },
      { metric: "100%", label: "Sync accuracy" },
    ],
    testimonial: {
      quote: "Their API integration services saved us thousands of hours of manual work. Great team to work with!",
      name: "Amit Patel",
      role: "Founder, EduTech Pro",
    },
    services: ["API Integration", "Dashboard Design", "Webhook Orchestration"],
  },
  {
    slug: "northwind-crm",
    title: "Northwind CRM",
    category: "Automation",
    desc: "Sales pipeline automation with email drip campaigns and lead scoring for B2B SaaS.",
    tag: "CRM",
    impact: "+38% close rate",
    client: "Northwind Co",
    industry: "B2B SaaS Sales",
    duration: "4 weeks",
    image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=1400&h=800&fit=crop",
    gallery: [
      "https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=900&h=600&fit=crop",
      "https://images.unsplash.com/photo-1551836022-d5d88e9218df?w=900&h=600&fit=crop",
      "https://images.unsplash.com/photo-1556761175-5973dc0f32e7?w=900&h=600&fit=crop",
    ],
    challenge: "Northwind's reps were spending 60% of their day on data entry and follow-ups. Leads were going cold because of inconsistent touchpoints.",
    solution: "Implemented a lead-scoring model based on activity + firmographics, built a 12-step automated email sequence triggered by score thresholds, and connected Slack alerts for hot leads.",
    results: [
      { metric: "+38%", label: "Close rate" },
      { metric: "−54%", label: "Time on admin" },
      { metric: "8 min", label: "Avg response time" },
      { metric: "+128%", label: "Pipeline value" },
    ],
    testimonial: {
      quote: "Optivize automated the boring half of our jobs so we could focus on closing. Our quota attainment went from 60% to 96%.",
      name: "Sandeep Iyer",
      role: "VP Sales, Northwind",
    },
    services: ["Automation", "Lead Scoring", "Email Sequences"],
  },
  {
    slug: "lumen-studios",
    title: "Lumen Studios",
    category: "AI Website",
    desc: "AI-generated branding website with copy, SEO and motion design delivered in 2 days.",
    tag: "AI",
    impact: "2-day launch",
    client: "Lumen Studios",
    industry: "Creative Agency",
    duration: "2 days",
    image: "https://images.unsplash.com/photo-1620712943543-bcc4688e7485?w=1400&h=800&fit=crop",
    gallery: [
      "https://images.unsplash.com/photo-1677442136019-21780ecad995?w=900&h=600&fit=crop",
      "https://images.unsplash.com/photo-1518770660439-4636190af475?w=900&h=600&fit=crop",
      "https://images.unsplash.com/photo-1633356122544-f134324a6cee?w=900&h=600&fit=crop",
    ],
    challenge: "Lumen needed a portfolio site live before pitching their first major client — 48 hours away. Hiring a freelance designer wasn't feasible.",
    solution: "We ran their brief through our AI Website Builder, generated 3 candidate layouts in 10 minutes, refined the winner with our designer, and deployed on day 2 with custom motion design.",
    results: [
      { metric: "48h", label: "From brief to live" },
      { metric: "1", label: "Pitch won" },
      { metric: "97/100", label: "PageSpeed score" },
      { metric: "5★", label: "Client satisfaction" },
    ],
    testimonial: {
      quote: "Optivize launched our entire SaaS site in two days. The AI builder is a game-changer for solo founders.",
      name: "Ananya R.",
      role: "Founder, Lumen Studios",
    },
    services: ["AI Website Builder", "Motion Design", "Brand Identity"],
  },
  {
    slug: "helix-labs-portal",
    title: "Helix Labs Portal",
    category: "SaaS App",
    desc: "Customer portal with billing, support tickets and real-time analytics dashboards.",
    tag: "Portal",
    impact: "+19 NPS",
    client: "Helix Labs",
    industry: "Developer Tools",
    duration: "10 weeks",
    image: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=1400&h=800&fit=crop",
    gallery: [
      "https://images.unsplash.com/photo-1551434678-e076c223a692?w=900&h=600&fit=crop",
      "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=900&h=600&fit=crop",
      "https://images.unsplash.com/photo-1517245386807-bb43f82c33c4?w=900&h=600&fit=crop",
    ],
    challenge: "Helix had a fragmented support experience — tickets in Intercom, billing in Stripe, usage stats in their own app. Customers complained about the runaround.",
    solution: "Built a unified customer portal with SSO across all systems, real-time usage analytics, embedded billing self-serve, and a knowledge base. Single login, full visibility.",
    results: [
      { metric: "+19", label: "NPS lift" },
      { metric: "−63%", label: "Support tickets" },
      { metric: "92%", label: "Self-serve rate" },
      { metric: "+24%", label: "Expansion revenue" },
    ],
    testimonial: {
      quote: "Best ROI we've had on any project. Customers love it and our support team thanks us every week.",
      name: "Marco D.",
      role: "CTO, Helix Labs",
    },
    services: ["SaaS Portal", "SSO", "Real-time Analytics"],
  },
];

export const findProject = (slug) => PROJECTS.find(p => p.slug === slug);
